/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useMemo, useState } from "react";
import {
  createBlackoutDayOfWeekFromGroup,
  DayOfWeek,
  EditableBlackoutDayOfWeek,
  EditableBlackoutDayOfWeekRuleGroup,
} from "./blackoutDayOfWeek";
import { DayOfWeekToggle } from "./DayOfWeekToggle";
import { TimeRangeInput } from "../validation/timeRange/TimeRangeInput";
import { TimeFormat } from "../../../forms/elements/timeInputUtils";
import { getResource } from "../../../language/resources/getResource";
import { formatRuleIds } from "../../module";
import { ROW_GROUP } from "../../../forms/elements/LabelGroup";
import trashcan from "../../../assets/icons/trashcan.svg";
import {
  deleteButtonCss,
  inputBaseCss,
  rowContainer,
} from "../ruleEditorStyles";
import { ValidationIndicator } from "../validation/ValidationIndicator";
import { shouldShowSubmitValidationForRow } from "../validation/validationUtils";

const daysCss = css`
  margin-left: 10px;
  margin-right: 10px;
  display: flex;
  gap: 4px;
`;

const timeRangeCss = css`
  width: 9.4rem;
  flex: 0 0 9.4rem;
`;

type Props = {
  resources: Record<string, any>;
  timeFormat: TimeFormat;
  ruleGroup: EditableBlackoutDayOfWeekRuleGroup;
  days: DayOfWeek[];
  onChange: (ruleGroup: EditableBlackoutDayOfWeekRuleGroup) => void;
  onDelete: (ruleGroup: EditableBlackoutDayOfWeekRuleGroup) => void;
  submitCount: number;
  hasChangesSinceSubmit: boolean;
};

/**
 * This row represents a group of blackout day of week rules that share the same
 * time slot range (start/end time), with one rule created or updated for each
 * selected day of the week.
 *
 * Editing model: local draft state with parent reconciliation.
 *
 * The row maintains local state for user interaction (day toggles and time
 * inputs) and commits changes upward via onChange / onDelete.
 *
 * The parent editor owns the source of truth, identity reconciliation, and
 * diffing (created / updated / deleted).
 *
 * Local draft state re-syncs only when the rule group identity changes.
 */
export function BlackoutDayOfWeekRuleRow({
  resources,
  timeFormat,
  ruleGroup,
  days,
  onChange,
  onDelete,
  submitCount,
  hasChangesSinceSubmit,
}: Props) {
  const [localRuleGroup, setLocalRuleGroup] =
    useState<EditableBlackoutDayOfWeekRuleGroup>(ruleGroup);

  useEffect(() => {
    setLocalRuleGroup(ruleGroup);

    // Intentional - only reset local state when the specific rule changes, not on every prop change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleGroup.id]);

  // Local state for day toggle, time inputs
  const localSelectedDayIds = useMemo(
    () =>
      new Set(
        localRuleGroup.rules
          .filter((r) => r.isSelected && r.dayOfWeekId != null)
          .map((r) => r.dayOfWeekId!),
      ),
    [localRuleGroup.rules],
  );

  const ruleGroupIsEmpty = localSelectedDayIds.size === 0;

  const showSubmitValidation = shouldShowSubmitValidationForRow(
    submitCount,
    hasChangesSinceSubmit,
    ruleGroupIsEmpty,
  );

  const hasValidationError = showSubmitValidation && ruleGroupIsEmpty;

  const ruleIds = formatRuleIds(localRuleGroup.rules);
  const hasRuleIds = ruleIds.length > 0;
  const timeRangeTitle = hasRuleIds
    ? getResource(resources, "ACCESS_RULE_EDIT_RULE_ID", ruleIds)
    : getResource(resources, "ACCESS_RULE_EDIT_NEW_RULE");

  // Handle User action: toggle day selection
  const handleToggleDay = (dayId: number) => {
    const hasRuleForDay = localRuleGroup.rules.some(
      (rule) => rule.dayOfWeekId === dayId,
    );

    let updatedRules: EditableBlackoutDayOfWeek[];

    if (hasRuleForDay) {
      // Toggle selection on existing rules
      updatedRules = localRuleGroup.rules.map((rule) =>
        rule.dayOfWeekId === dayId
          ? { ...rule, isSelected: !rule.isSelected }
          : rule,
      );
    } else {
      // Add a new rule for this day
      const newRule = createBlackoutDayOfWeekFromGroup(localRuleGroup.id, {
        dayOfWeekId: dayId,
      });

      updatedRules = [
        ...localRuleGroup.rules,
        { ...newRule, isSelected: true },
      ];
    }

    const updatedGroup: EditableBlackoutDayOfWeekRuleGroup = {
      ...localRuleGroup,
      rules: updatedRules,
    };

    setLocalRuleGroup(updatedGroup);
    onChange(updatedGroup);
  };

  const handleBlackoutTimeSlotChange = ({
    start,
    end,
  }: {
    start: Date | null;
    end: Date | null;
  }) => {
    const updatedGroup = {
      ...localRuleGroup,
      blackoutTimeSlotStart: start,
      blackoutTimeSlotEnd: end,
    };

    setLocalRuleGroup(updatedGroup);
    onChange(updatedGroup);
  };

  const handleDelete = () => {
    const deletedGroup = {
      ...localRuleGroup,
      isDeleted: true,
      rules: localRuleGroup.rules.map((rule) => ({
        ...rule,
        isSelected: false,
      })),
    };

    onDelete(deletedGroup);
  };

  return (
    <div css={rowContainer}>
      <div css={daysCss}>
        {days.map((day) => (
          <DayOfWeekToggle
            key={day.id}
            label={day.label}
            active={localSelectedDayIds.has(day.id)}
            onToggle={() => handleToggleDay(day.id)}
          />
        ))}
      </div>

      <ValidationIndicator
        severity="error"
        message={getResource(
          resources,
          "ACCESS_RULE_BLACKOUT_DAY_OF_WEEK_EMPTY_GROUP_ERROR",
        )}
        visible={hasValidationError}
      />

      <TimeRangeInput
        id={localRuleGroup.id}
        resources={resources}
        start={localRuleGroup.blackoutTimeSlotStart}
        startPlaceholder={getResource(resources, "ACCESS_RULE_EDIT_TIME_START")}
        end={localRuleGroup.blackoutTimeSlotEnd}
        endPlaceholder={getResource(resources, "ACCESS_RULE_EDIT_TIME_END")}
        title={timeRangeTitle}
        format={timeFormat}
        onChange={handleBlackoutTimeSlotChange}
        // Highlights a row the user never touched, so the save has something to point at.
        forceValidationVersion={submitCount}
        clearValidation={hasChangesSinceSubmit}
        showDash
        timeInputCss={css`
          ${timeRangeCss};
          ${inputBaseCss}
        `}
        rootCss={css`
          ${ROW_GROUP}
          margin-left: 15px;
        `}
      />
      <button
        type="button"
        tabIndex={-1}
        css={deleteButtonCss}
        onClick={handleDelete}
        title={getResource(resources, "ACCESS_RULE_DELETE")}
      >
        <img src={trashcan} alt="delete" />
      </button>
    </div>
  );
}
